import { useState } from 'react';

import type { JiraUniqueTicketInfo } from '../../../../../shared/issuesV2';
import { mod } from '../../../../utils';

export type UniqueTicketEditor = {
  isEnabled: boolean;
  setIsEnabled: (isEnabled: boolean) => void;

  tickets: JiraUniqueTicketInfo[];
  setTickets: (tickets: JiraUniqueTicketInfo[]) => void;

  title: string;
  setTitle: (title: string) => void;
  content: string;
  setContent: (content: string) => void;

  currentTicket: number;
  nextTicket: () => void;
  prevTicket: () => void;
};

/**
 * Hook that encapsulates the logic of navigating between unique tickets
 * and reading / writing their contents.
 */
const useUniqueTicketEditor = (): UniqueTicketEditor => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [uniqueTickets, setUniqueTickets] = useState<JiraUniqueTicketInfo[]>([]);
  const [currentTicketIndex, setCurrentTicketIndex] = useState<number>(0);

  // update the ticket at a specific index
  const updateTicket = (newTicket: JiraUniqueTicketInfo, at: number) => {
    const tickets = uniqueTickets.map((existingTicket, index) => {
      return index === at ? newTicket : existingTicket;
    });
    setUniqueTickets(tickets);
  };

  const setTickets = (tickets: JiraUniqueTicketInfo[]) => {
    setUniqueTickets(tickets);
    setCurrentTicketIndex(0);
  };

  const setTitle = (title: string) => {
    const currentTicket = uniqueTickets[currentTicketIndex] as JiraUniqueTicketInfo;
    const newTicket: JiraUniqueTicketInfo = {
      title,
      content: currentTicket.content,
      issueId: currentTicket.issueId,
    };
    updateTicket(newTicket, currentTicketIndex);
  };

  const setContent = (content: string) => {
    const currentTicket = uniqueTickets[currentTicketIndex] as JiraUniqueTicketInfo;
    const newTicket: JiraUniqueTicketInfo = {
      title: currentTicket.title,
      content,
      issueId: currentTicket.issueId,
    };
    updateTicket(newTicket, currentTicketIndex);
  };

  const nextTicket = () => {
    setCurrentTicketIndex(mod(currentTicketIndex + 1, uniqueTickets.length));
  };

  const prevTicket = () => {
    setCurrentTicketIndex(mod(currentTicketIndex - 1, uniqueTickets.length));
  };

  return {
    isEnabled,
    setIsEnabled,

    tickets: uniqueTickets,
    setTickets,

    title: uniqueTickets[currentTicketIndex]?.title ?? '',
    setTitle,
    content: uniqueTickets[currentTicketIndex]?.content ?? '',
    setContent,

    currentTicket: currentTicketIndex + 1,
    nextTicket,
    prevTicket,
  };
};

export default useUniqueTicketEditor;
